import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import dayjs from 'dayjs'
import Image from 'next/image'
import type {FC} from 'react'
import {useMemo} from 'react'
import {Container} from 'src/common/Container'
import {DeferredVideo} from 'src/common/DeferredVideo'
import {getButtonsData, getMediaLinkUrl} from 'src/common/helpers'
import {useGetBlogPostCategory} from 'src/common/hooks/useGetBlogPostCategory'
import {ThreeColumnLayout} from 'src/common/ThreeColumnLayout'
import type {Context} from 'src/common/types'
import {ArticleContent} from './components/ArticleContent'
import {ArticleHeader} from './components/ArticleHeader'
import {ArticleSEO} from './components/ArticleSEO'
import {AuthorBio} from './components/AuthorBio'
import {Breadcrumbs} from './components/Breadcrumbs'
import {CTASection} from './components/CTASection'
import {TableOfContents} from './components/TableOfContents'
import {extractHeadingsFromContent, generateArticleStructuredData, generateFaqStructuredData} from './utils'

/**
 * Props for `BlogPost`.
 */
export type BlogPostProps = SliceComponentProps<Content.BlogPostSlice> & {context: Context}

/**
 * Component for "BlogPost" Slices.
 */
const BlogPost: FC<BlogPostProps> = ({slice: {primary}, context}) => {
  // Prepare data
  const formattedDate = context.publishedDate ? dayjs(context.publishedDate).format('MMMM D, YYYY') : ''

  const sections = {
    takeaways: primary.takeaways_section,
    list: primary.list_section,
    banner: primary.banner,
    faq: primary.faq_section,
    table: primary.table,
    skillDownloadLink: primary.skill_download_link,
    chatgptPromptDownloadLink: primary.chatgpt_prompt_download_link,
    githubRepositoryLink: primary.github_repository_link,
  }

  const {primaryButtonData} = getButtonsData({
    buttonsData: context.buttonsData,
    primaryButton: primary.sidebar[0]?.button,
  })

  // Extract headings for table of contents
  const headings = useMemo(() => extractHeadingsFromContent(primary.content), [primary.content])
  const category = useGetBlogPostCategory(context.category)

  // Generate structured data for SEO
  const articleStructuredData = useMemo(
    () =>
      generateArticleStructuredData({
        title: primary.title,
        imageUrl: primary.image.url || '',
        author: primary.author,
        publishedDate: context.publishedDate,
        category,
        description: context.description ?? undefined,
      }),
    [primary.title, primary.image.url, primary.author, context.publishedDate, category, context.description],
  )

  const faqStructuredData = useMemo(() => generateFaqStructuredData(primary.faq_section), [primary.faq_section])

  const videoUrl = getMediaLinkUrl(primary.video)

  return (
    <>
      <ArticleSEO structuredData={articleStructuredData} faqStructuredData={faqStructuredData} />

      <Container className="!overflow-visible !pt-[48px] md:!bg-moss-grey-100 md:!pt-[72px]">
        <div className="[1100px]:pb-20 mx-auto w-full max-w-[1200px] rounded-[4px] bg-moss-white pb-5 pt-12 max-[1100px]:mx-auto max-[1100px]:w-fit md:pb-10 md:max-[1100px]:px-14">
          <ThreeColumnLayout
            leftSidebar={<TableOfContents headings={headings} title={primary.sidebar[0]?.title} />}
            leftSidebarTopPadding="pt-[172px]"
            rightSidebar={
              <CTASection
                ratingPlatform={primary.sidebar[0]?.rating_platform}
                rating={primary.sidebar[0]?.rating}
                ctaTitle={primary.sidebar[0]?.cta_title}
                buttonData={primaryButtonData}
                pageUid={context.pageUid}
              />
            }
            rightSidebarTopPadding="pt-[172px]"
            mainContent={
              <>
                {(isFilled.richText(primary.title) ||
                  isFilled.contentRelationship(primary.author) ||
                  isFilled.keyText(primary.author_tag) ||
                  (isFilled.contentRelationship(context.parentPage) && !!context.parentPage.data) ||
                  isFilled.keyText(primary.reading_time) ||
                  category) && (
                  <div className="flex flex-col gap-8">
                    <div className="flex flex-col gap-1">
                      <Breadcrumbs parentPage={context.parentPage} title={context.pageUid} />
                    </div>
                    <ArticleHeader
                      title={primary.title}
                      author={primary.author}
                      authorTag={primary.author_tag}
                      category={category}
                      formattedDate={formattedDate}
                      readingTime={primary.reading_time}
                    />
                  </div>
                )}
                {/* Featured Video or Image */}
                {videoUrl ? (
                  <div className="[1100px]:mb-4 mb-0 aspect-video w-full overflow-hidden rounded-lg max-md:pb-4 md:mb-6">
                    <DeferredVideo
                      videoUrl={videoUrl}
                      posterUrl={isFilled.image(primary.video_poster) ? primary.video_poster.url : primary.image.url}
                      className="size-full object-cover"
                    />
                  </div>
                ) : (
                  isFilled.image(primary.image) && (
                    <div className="[1100px]:mb-4 mb-0 w-full overflow-hidden rounded-lg max-md:pb-4 md:mb-6">
                      <Image
                        src={primary.image.url}
                        className="h-auto w-full"
                        alt={primary.image?.alt || 'Main image'}
                        width={600}
                        height={345}
                      />
                    </div>
                  )
                )}
                {/* Mobile Table of Contents */}
                {headings.length > 0 && (
                  <div className="mb-4 min-[1100px]:hidden">
                    <div className="flex flex-col gap-6 rounded-lg border border-moss-black/[10%] bg-moss-white p-6">
                      <TableOfContents headings={headings} title={primary.sidebar[0]?.title} />
                    </div>
                  </div>
                )}

                {/* Article Content */}
                <div className="flex flex-col gap-4" id="article-content">
                  <ArticleContent content={primary.content} sections={sections} buttonsData={context.buttonsData} />
                </div>

                {/* Author Bio */}
                <AuthorBio author={primary.author} authorTitle={primary.author_title} />
              </>
            }
          />
        </div>
      </Container>
    </>
  )
}

export default BlogPost
